import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Box, Text, Badge, useToast, Flex, IconButton, Select, Input,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiBookOpen } from 'react-icons/fi';
import api from '../api';
import ConfirmModal from './ConfirmModal';

export default function ResourceBookingsDashboard({ isOpen, onClose, onNavigateToProject, onManageResources }) {
  const [bookings, setBookings] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [invoices, setInvoices] = useState({});
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const [dateFilter, setDateFilter] = useState('upcoming');
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, title }

  const fetchBookings = useCallback(async () => {
    try {
      // Build date params using the same working endpoint as the task modal
      const today = new Date().toISOString().split('T')[0];
      const past90 = new Date(); past90.setDate(past90.getDate() - 90);
      const ahead90 = new Date(); ahead90.setDate(ahead90.getDate() + 90);
      let from, to;
      if (dateFilter === 'past') { from = past90.toISOString().split('T')[0]; to = today; }
      else if (dateFilter === 'all') { from = past90.toISOString().split('T')[0]; to = ahead90.toISOString().split('T')[0]; }
      else if (dateFilter === 'deleted') { from = today; to = ahead90.toISOString().split('T')[0]; }
      else { from = today; to = ahead90.toISOString().split('T')[0]; }
      const includeDeleted = dateFilter === 'deleted' || dateFilter === 'past';
      const { data } = await api.get(`/resource-bookings?from=${from}&to=${to}&include_details=true${includeDeleted ? '&include_deleted=true' : ''}`);
      setBookings(Array.isArray(data) ? data : []);
    } catch(e) { setBookings([]); }
  }, [dateFilter]);

  useEffect(() => { if (isOpen) { fetchBookings(); setEditingId(null); } }, [isOpen, fetchBookings]);
  const fmtTime = (t) => t ? t.slice(0, 5) : '';
  const todayDate = new Date().toISOString().split('T')[0];
  const isPastDate = (d) => d && d.split('T')[0] < todayDate;

  const getBookingStyle = (b) => {
    if (b.deleted_at) return {
      borderColor: 'gray.200', bg: 'gray.50',
      barColor: 'gray.300', textColor: 'gray.400', isDeleted: true,
    };
    if (isPastDate(b.start_date)) return {
      borderColor: 'gray.300', bg: 'gray.50',
      barColor: 'gray.400', textColor: 'gray.500'
    };
    if (b.status === 'tentative') return {
      borderColor: 'gray.300', bg: 'gray.50',
      barColor: 'gray.500', textColor: 'gray.700'
    };
    // confirmed / default
    return {
      borderColor: 'green.200', bg: 'white',
      barColor: 'green.400', textColor: 'gray.800'
    };
  };

  const StatusBadge = ({ b }) => {
    if (isPastDate(b.start_date))
      return <Badge fontSize="8px" bg="gray.800" color="white" textTransform="capitalize">Previous Date</Badge>;
    if (b.status === 'tentative')
      return <Badge fontSize="8px" colorScheme="orange" textTransform="capitalize">Tentative</Badge>;
    return <Badge fontSize="8px" colorScheme="green" textTransform="capitalize">Confirmed</Badge>;
  };

  // Filter for the "deleted" view: only show deleted; for "past": show all (live + deleted)
  const visibleBookings = dateFilter === 'deleted'
    ? bookings.filter(b => b.deleted_at)
    : bookings;

  // Group by day
  const grouped = {};
  visibleBookings.forEach(b => {
    const d = b.start_date?.split('T')[0] || 'unknown';
    if (!grouped[d]) grouped[d] = [];
    grouped[d].push(b);
  });

  const todayStr = new Date().toISOString().split('T')[0];
  const tomorrowStr = new Date(Date.now() + 86400000).toISOString().split('T')[0];
  const dayLabel = (d) => {
    if (d === todayStr) return 'Today';
    if (d === tomorrowStr) return 'Tomorrow';
    return new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const startEdit = (b) => {
    setEditForm({
      title: b.title || '', description: b.description || '', status: b.status || 'confirmed',
      start_time: b.start_time?.slice(0,5) || '', end_time: b.end_time?.slice(0,5) || '',
    });
    setEditingId(b.id);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/resource-bookings/${editingId}`, editForm);
      await fetchBookings(); setEditingId(null);
      toast({ title: 'Updated', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const fetchInvoicesForProject = async (projectId) => {
    if (invoices[projectId]) return;
    try {
      const { data } = await api.get(`/invoices?project_id=${projectId}&direction=out`);
      const list = Array.isArray(data) ? data : (data.data || []);
      setInvoices(prev => ({ ...prev, [projectId]: list }));
    } catch(e) {}
  };

  const linkInvoice = async (bookingId, invoiceId) => {
    try {
      await api.put(`/resource-bookings/${bookingId}/invoice`, { invoice_id: invoiceId || null });
      await fetchBookings();
      toast({ title: invoiceId ? 'Invoice linked' : 'Invoice unlinked', status: 'success', duration: 1500 });
    } catch(e) {}
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex justify="space-between" align="center" pr={8}>
            <HStack><FiBookOpen /><Text>Resource Bookings</Text></HStack>
            <HStack spacing={2}>
              <Select size="xs" w="160px" value={dateFilter} onChange={e => setDateFilter(e.target.value)}>
                <option value="upcoming">Upcoming (90 days)</option>
                <option value="past">Past (90 days)</option>
                <option value="all">All bookings</option>
                <option value="deleted">Deleted (future)</option>
              </Select>
              <Button size="xs" variant="outline" colorScheme="teal" onClick={() => onManageResources && onManageResources()}>Manage Resources</Button>
            </HStack>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          {Object.keys(grouped).length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>No upcoming bookings</Text>
          ) : (
            <VStack spacing={4} align="stretch">
              {Object.entries(grouped).map(([date, dayBookings]) => (
                <Box key={date}>
                  <Text fontSize="13px" fontWeight="700" color={date === todayStr ? 'teal.600' : date === tomorrowStr ? 'blue.600' : 'gray.600'}
                    mb={2} borderBottom="2px" borderColor={date === todayStr ? 'teal.400' : date === tomorrowStr ? 'blue.300' : 'gray.200'} pb={1}>
                    {dayLabel(date)}
                  </Text>
                  <VStack spacing={1} align="stretch">
                    {dayBookings.map(b => (
                      <Box key={b.id} p={2} border="1px"
                        borderColor={b.is_annual_leave ? 'green.300' : getBookingStyle(b).borderColor}
                        borderRadius="md"
                        bg={b.is_annual_leave ? 'green.50' : getBookingStyle(b).bg}
                        _hover={{ bg: b.is_annual_leave ? 'green.100' : 'gray.100' }}>
                        {b.is_annual_leave ? (
                          <Flex align="center" gap={3}>
                            <Box w="4px" flexShrink={0} alignSelf="stretch" borderRadius="full" bg="green.400" minH="32px" />
                            <Box flex={1}>
                              <HStack spacing={2} mb={0.5}>
                                <Badge colorScheme="green" fontSize="8px" textTransform="uppercase">
                                  {b.leave_type === 'annual' ? 'Annual Leave'
                                    : b.leave_type === 'unpaid' ? 'Unpaid Leave'
                                    : b.leave_type === 'compassionate' ? 'Compassionate'
                                    : b.leave_type === 'maternity' ? 'Maternity'
                                    : b.leave_type === 'paternity' ? 'Paternity'
                                    : 'Leave'}
                                </Badge>
                                <Text fontSize="12px" fontWeight="700" color="green.800">{b.title}</Text>
                              </HStack>
                              {b.description && <Text fontSize="10px" color="green.700" fontStyle="italic">{b.description}</Text>}
                              <HStack spacing={2} mt={0.5}>
                                {(b.users || []).map(u => (
                                  <Badge key={u.user_id} colorScheme="green" variant="subtle" fontSize="8px">
                                    👤 {u.display_name}
                                  </Badge>
                                ))}
                                <Badge colorScheme="gray" fontSize="8px" variant="outline">Unavailable all day</Badge>
                              </HStack>
                            </Box>
                          </Flex>
                        ) : editingId === b.id ? (
                          <VStack spacing={2} align="stretch">
                            <HStack spacing={2}>
                              <Input size="xs" flex={2} value={editForm.title} placeholder="Title"
                                onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))} />
                              <Input size="xs" flex={1} type="time" value={editForm.start_time}
                                onChange={e => setEditForm(p => ({ ...p, start_time: e.target.value }))} />
                              <Input size="xs" flex={1} type="time" value={editForm.end_time}
                                onChange={e => setEditForm(p => ({ ...p, end_time: e.target.value }))} />
                              <Select size="xs" flex={1} value={editForm.status}
                                onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))}>
                                <option value="confirmed">Confirmed</option><option value="tentative">Tentative</option>
                                <option value="cancelled">Cancelled</option>
                              </Select>
                            </HStack>
                            <Input size="xs" value={editForm.description} placeholder="Description"
                              onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} />
                            <HStack spacing={1} justifyContent="flex-end">
                              <Button size="xs" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                              <Button size="xs" colorScheme="teal" onClick={handleSaveEdit} isLoading={saving}>Save</Button>
                            </HStack>
                          </VStack>
                        ) : (
                          <Flex justify="space-between" align="start">
                            <Box flex={1}>
                              <HStack spacing={2} mb={0.5}>
                                <StatusBadge b={b} />
                                {b.start_time && (
                                  <Text fontSize="11px" fontWeight="700" color={isPastDate(b.start_date) ? "gray.400" : b.status === "tentative" ? "gray.500" : "teal.600"}>
                                    {fmtTime(b.start_time)}{b.end_time ? ` – ${fmtTime(b.end_time)}` : ''}
                                  </Text>
                                )}
                                <Text fontSize="12px" fontWeight="600">{b.title || b.task_title || '(untitled)'}</Text>
                              </HStack>
                              {b.description && <Text fontSize="10px" color="gray.500" mb={1}>{b.description}</Text>}
                              <HStack spacing={2} fontSize="10px" color="gray.500" flexWrap="wrap">
                                {b.project_name && <Text>Project: <b>{b.project_name}</b></Text>}
                                {b.task_title && <Text>Task: <b>{b.task_title}</b></Text>}
                                {b.company_name && <Text>Company: <b>{b.company_name}</b></Text>}
                                {b.contact_first_name && <Text>Contact: <b>{b.contact_first_name} {b.contact_last_name || ''}</b></Text>}
                              </HStack>
                              {(b.resources || []).length > 0 && (
                                <Flex gap={1} mt={1} flexWrap="wrap">
                                  {b.resources.map(r => (
                                    <Badge key={r.resource_id} colorScheme="teal" fontSize="8px" variant="subtle">
                                      {r.resource_code ? `${r.resource_code}` : r.resource_name}
                                    </Badge>
                                  ))}
                                </Flex>
                              )}
                              <HStack mt={1} spacing={1}>
                                {b.invoice_id ? (
                                  <Badge colorScheme="green" fontSize="8px">
                                    Invoice: {b.invoice_number || `#${b.invoice_id}`} (£{parseFloat(b.invoice_total || 0).toFixed(2)})
                                  </Badge>
                                ) : (
                                  <HStack spacing={1}>
                                    <Select size="xs" w="180px" placeholder="Link invoice..." fontSize="10px" value=""
                                      onFocus={() => fetchInvoicesForProject(b.project_id)}
                                      onChange={e => { if (e.target.value) linkInvoice(b.id, parseInt(e.target.value)); }}>
                                      {(invoices[b.project_id] || []).map(inv => (
                                        <option key={inv.id} value={inv.id}>{inv.invoice_number || `#${inv.id}`} £{parseFloat(inv.total_gross || 0).toFixed(2)}</option>
                                      ))}
                                    </Select>
                                  </HStack>
                                )}
                              </HStack>
                            </Box>
                            <HStack spacing={0} ml={2}>
                              {!b.deleted_at && <IconButton icon={<FiEdit2 />} size="xs" variant="ghost" onClick={() => startEdit(b)} aria-label="Edit" />}
                              {b.deleted_at ? (
                                <Badge colorScheme="gray" fontSize="8px" px={2}>DELETED</Badge>
                              ) : (
                                <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                                  onClick={() => setDeleteTarget({ id: b.id, title: b.title || `${b.start_date?.split('T')[0]} booking` })}
                                  aria-label="Delete" />
                              )}
                            </HStack>
                          </Flex>
                        )}
                      </Box>
                    ))}
                  </VStack>
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
    <ConfirmModal
      isOpen={!!deleteTarget}
      onClose={() => setDeleteTarget(null)}
      title="Delete Resource Booking"
      message={`Are you sure you want to delete "${deleteTarget?.title}"? This cannot be undone.`}
      confirmLabel="Delete"
      confirmColor="red"
      onConfirm={async () => {
        try { await api.delete(`/resource-bookings/${deleteTarget.id}`); await fetchBookings(); toast({ title: 'Booking deleted', status: 'info', duration: 1500 }); }
        catch(e) { toast({ title: 'Failed to delete', status: 'error', duration: 2000 }); }
        setDeleteTarget(null);
      }}
    />
    </>
  );
}
